import { useEffect, useState, FC } from "react";

import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import {
  PartnerCustomFilter,
  PartnerOrgModel,
  patchPartnerOrg,
} from "@rubi/api-generated";

import {
  CustomFilterDraft,
  emptyCustomFilterDraft,
  fromCustomFilterDraft,
  parseFilterOptions,
  toCustomFilterDraft,
} from "./customFilterDrafts";

export const PartnerCustomFiltersEditor: FC<{
  partnerOrgId: string;
  customFilters: PartnerCustomFilter[];
  onSaved: (partnerOrg: PartnerOrgModel) => void;
}> = ({ partnerOrgId, customFilters, onSaved }) => {
  const [drafts, setDrafts] = useState<CustomFilterDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(customFilters.map(toCustomFilterDraft));
  }, [customFilters]);

  const isDirty =
    JSON.stringify(drafts.map(fromCustomFilterDraft)) !==
    JSON.stringify(
      customFilters.map(toCustomFilterDraft).map(fromCustomFilterDraft),
    );

  const updateDraft = (index: number, change: Partial<CustomFilterDraft>) =>
    setDrafts(drafts.map((d, i) => (i === index ? { ...d, ...change } : d)));

  const moveDraft = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= drafts.length) return;
    const next = [...drafts];
    [next[index], next[target]] = [next[target], next[index]];
    setDrafts(next);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await patchPartnerOrg({
        path: { partner_org_id: partnerOrgId },
        body: { custom_filters: drafts.map(fromCustomFilterDraft) },
        throwOnError: false,
      });
      if (response.status === 200) {
        onSaved(response.data!);
      } else {
        setSaveError("" + response.status + " " + response.error?.console_msg);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      {drafts.length === 0 && (
        <span className="muted">
          No custom filters. Partners only see the built-in filters.
        </span>
      )}
      {drafts.map((draft, index) => {
        const hasOptions = parseFilterOptions(draft.optionsText) !== null;
        return (
          <Box
            key={index}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Label"
                  value={draft.label}
                  onChange={(e) =>
                    updateDraft(index, { label: e.target.value })
                  }
                  helperText="Shown to partners, e.g. Store"
                  fullWidth
                />
                <TextField
                  label="Id"
                  value={draft.id}
                  onChange={(e) => updateDraft(index, { id: e.target.value })}
                  helperText="Lowercase letters, digits or _. Changing it clears saved selections."
                  fullWidth
                />
              </Stack>
              <TextField
                label="Field key"
                value={draft.fieldKey}
                onChange={(e) =>
                  updateDraft(index, { fieldKey: e.target.value })
                }
                helperText="Report answer path, e.g. business-lcboLocation.store"
                fullWidth
              />
              <TextField
                label="Dropdown options"
                value={draft.optionsText}
                onChange={(e) =>
                  updateDraft(index, { optionsText: e.target.value })
                }
                helperText="One per line: value, or value,label. Leave empty for a free-text filter."
                multiline
                minRows={3}
                maxRows={12}
                fullWidth
              />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasOptions && draft.multiple}
                      disabled={!hasOptions}
                      onChange={(e) =>
                        updateDraft(index, { multiple: e.target.checked })
                      }
                    />
                  }
                  label="Allow multiple selections"
                />
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  disabled={index === 0}
                  onClick={() => moveDraft(index, -1)}
                >
                  Move up
                </Button>
                <Button
                  size="small"
                  disabled={index === drafts.length - 1}
                  onClick={() => moveDraft(index, 1)}
                >
                  Move down
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    setDrafts(drafts.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </Button>
              </Stack>
            </Stack>
          </Box>
        );
      })}
      {saveError && <Alert severity="error">{saveError}</Alert>}
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button
          onClick={() => setDrafts([...drafts, { ...emptyCustomFilterDraft }])}
        >
          Add filter
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? "Saving…" : "Save filters"}
        </Button>
      </Stack>
    </Stack>
  );
};
